import { useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Link, useSearchParams } from 'react-router-dom'

import { submitInquiry } from '@/lib/api'
import { inquiryFormSchema, type InquiryFormSchema } from '@/lib/schemas'

export function InquiryForm() {
  const [submitted, setSubmitted] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [searchParams] = useSearchParams()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<InquiryFormSchema>({
    resolver: zodResolver(inquiryFormSchema),
    defaultValues: { guests: 2, cabinId: searchParams.get('cabin') ?? '' },
  })

  async function onSubmit(data: InquiryFormSchema) {
    setServerError(null)
    const res = await submitInquiry(data)
    if (res.success) {
      setSubmitted(true)
    } else {
      setServerError(res.error)
    }
  }

  if (submitted) {
    return (
      <div className="border border-cream-300 bg-cream-200 px-8 py-10 text-center">
        <p className="text-[10px] tracking-[0.3em] uppercase text-ink-muted font-semibold mb-3">Received</p>
        <h2 className="text-xl font-semibold text-ink">Thank you</h2>
        <p className="mt-2 text-sm text-ink-soft font-light leading-relaxed">
          We'll confirm availability and reach out within 24 hours.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
      {/* Name */}
      <div>
        <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-ink">
          Full Name *
        </label>
        <input id="name" type="text" className="input-field" {...register('name')} />
        {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>}
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-ink">
          Email *
        </label>
        <input id="email" type="email" className="input-field" {...register('email')} />
        {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
      </div>

      {/* Phone */}
      <div>
        <label htmlFor="phone" className="mb-1.5 block text-sm font-medium text-ink">
          Phone (optional)
        </label>
        <input id="phone" type="tel" className="input-field" {...register('phone')} />
      </div>

      {/* Dates */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="checkIn" className="mb-1.5 block text-sm font-medium text-ink">
            Check-in *
          </label>
          <input id="checkIn" type="date" className="input-field" {...register('checkIn')} />
          {errors.checkIn && (
            <p className="mt-1 text-xs text-red-600">{errors.checkIn.message}</p>
          )}
        </div>
        <div>
          <label htmlFor="checkOut" className="mb-1.5 block text-sm font-medium text-ink">
            Check-out *
          </label>
          <input id="checkOut" type="date" className="input-field" {...register('checkOut')} />
          {errors.checkOut && (
            <p className="mt-1 text-xs text-red-600">{errors.checkOut.message}</p>
          )}
        </div>
      </div>

      {/* Preferred Accommodation */}
      <div>
        <div className="mb-1.5 flex items-baseline justify-between">
          <label htmlFor="cabinId" className="block text-sm font-medium text-ink">
            Preferred Accommodation
          </label>
          <Link to="/treehouses" className="text-[10px] tracking-widest uppercase text-ink-muted hover:text-ink transition-colors">
            Browse Rooms →
          </Link>
        </div>
        <select id="cabinId" className="input-field" {...register('cabinId')}>
          <option value="">No preference / Not sure yet</option>
          <optgroup label="Treehouses">
            <option value="canopy-nest">The Canopy Nest — ₹8,500/night (up to 3 guests)</option>
            <option value="canopy-loft">The Canopy Loft — ₹13,500/night (up to 6 guests)</option>
          </optgroup>
          <optgroup label="Wooden Cabins">
            <option value="wooden-cabin-1">Wooden Cabin 1 — ₹11,500/night (up to 3 guests)</option>
            <option value="wooden-cabin-2">Wooden Cabin 2 — ₹11,500/night (up to 3 guests)</option>
            <option value="wooden-cabins-345">Wooden Cabins 3, 4 &amp; 5 — ₹11,500/night each (up to 3 guests each)</option>
            <option value="wooden-cabin-6">Wooden Cabin 6 (Family Suite) — ₹13,500/night (up to 6 guests)</option>
          </optgroup>
        </select>
      </div>

      {/* Guests */}
      <div>
        <label htmlFor="guests" className="mb-1.5 block text-sm font-medium text-ink">
          Number of Guests *
        </label>
        <input
          id="guests"
          type="number"
          min={1}
          max={10}
          className="input-field"
          {...register('guests', { valueAsNumber: true })}
        />
        {errors.guests && <p className="mt-1 text-xs text-red-600">{errors.guests.message}</p>}
      </div>

      {/* Message */}
      <div>
        <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-ink">
          Message (optional)
        </label>
        <textarea
          id="message"
          rows={4}
          className="input-field resize-none"
          placeholder="Any special requests, questions, or preferred treehouse?"
          {...register('message')}
        />
        {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message.message}</p>}
      </div>

      {/* Server error */}
      {serverError && (
        <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{serverError}</p>
      )}

      <button type="submit" disabled={isSubmitting} className="btn-primary w-full py-3.5">
        {isSubmitting ? 'Sending…' : 'Send Inquiry'}
      </button>
    </form>
  )
}
